import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { 
  FiUsers, FiCalendar, FiDollarSign, FiClock, FiCheckCircle, 
  FiAlertCircle, FiRefreshCw, FiBookOpen, FiUserCheck, FiUserX, FiPrinter
} from 'react-icons/fi';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function HRManagementPage() {
  const { user } = useAuth();
  
  // ตรวจสอบสิทธิ์ (เฉพาะ Owner หรือ IT เท่านั้นที่มีสิทธิ์เข้าถึงหน้า HR ได้)
  const isAuthorized = user?.role === 'owner' || user?.role === 'it' || user?.username === 'owner' || user?.username === 'it';

  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState('attendance'); // attendance, payroll
  const [isLoading, setIsLoading] = useState(true);

  // สเตตฝั่งลงเวลา (Attendance)
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceList, setAttendanceList] = useState([]); // รายการลงเวลาในระบบจริง
  const [attendanceNotes, setAttendanceNotes] = useState({}); // หมายเหตุ ขาด ลา มาสาย รายคน
  const [attendanceStatus, setAttendanceStatus] = useState({}); // สถานะ ขาด ลา มาสาย รายคน

  // สเตตฝั่งเงินเดือน (Payroll)
  const [payrollMonth, setPayrollMonth] = useState(() => {
    const today = new Date();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    return `${mm}-${today.getFullYear()}`;
  });
  const [payrolls, setPayrolls] = useState([]);
  const [evaluations, setEvaluations] = useState([]); // เอาไว้ลิงก์คำนวณโบนัสอ้างอิงจาก KPI

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'attendance') {
      fetchAttendanceLogs();
    } else {
      fetchPayrollData();
    }
  }, [activeTab, attendanceDate, payrollMonth, employees]);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      // 1. ดึงรายชื่อพนักงาน
      const empRes = await fetch(`${API_URL}?action=get_users`);
      const empData = await empRes.json();
      if (Array.isArray(empData)) {
        setEmployees(empData);
      }

      // 2. ดึงประวัติประเมิน KPI สำหรับคำนวณโบนัสอัตโนมัติ
      const evalRes = await fetch(`${API_URL}?action=get_evaluations`);
      const evalData = await evalRes.json();
      if (evalData.success && Array.isArray(evalData.data)) {
        setEvaluations(evalData.data);
      }
    } catch (err) {
      console.error("เกิดข้อผิดพลาดในการโหลดข้อมูลตั้งต้น HR:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // ดึงข้อมูล ขาด ลา มา สาย จาก SQL
  const fetchAttendanceLogs = async () => {
    try {
      const res = await fetch(`${API_URL}?action=get_attendance`);
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setAttendanceList(data.data);
        
        // กรองการลงเวลาของวันที่เลือกปัจจุบัน
        const todayLogs = data.data.filter(log => log.log_date === attendanceDate);
        
        // เซ็ตค่าสเตตเริ่มต้นในฟอร์มลงเวลา
        const newStatus = {};
        const newNotes = {};
        
        employees.forEach(emp => {
          const log = todayLogs.find(l => l.emp_uid === emp.uid);
          newStatus[emp.uid] = log ? log.status : 'present';
          newNotes[emp.uid] = log ? log.notes || '' : '';
        });
        
        setAttendanceStatus(newStatus);
        setAttendanceNotes(newNotes);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // ดึงข้อมูลบัญชีจ่ายเงินเดือน
  const fetchPayrollData = async () => {
    try {
      const res = await fetch(`${API_URL}?action=get_employee_payroll&month_year=${payrollMonth}`);
      const data = await res.json();
      
      if (data.success && Array.isArray(data.data)) {
        // หากมีรายการบันทึกเงินเดือนใน DB แล้ว
        const dbPayroll = data.data;
        const compiledPayroll = employees.map(emp => {
          const record = dbPayroll.find(r => r.emp_uid === emp.uid);
          
          if (record) {
            return {
              emp_uid: record.emp_uid,
              emp_name: record.emp_name,
              department: emp.department || 'ทีมช่างซ่อม',
              base_salary: parseFloat(record.base_salary),
              bonus: parseFloat(record.bonus),
              deduction: parseFloat(record.deduction),
              net_salary: parseFloat(record.net_salary),
              status: record.status,
              paidAt: record.paidAt
            };
          } else {
            // คำนวณหาแนะนำเริ่มต้น
            return calculateDefaultPayroll(emp);
          }
        });
        setPayrolls(compiledPayroll);
      } else {
        // หากยังไม่มีการบันทึก สร้างค่าแนะนำเริ่มต้นทั้งหมด
        const defaultPayroll = employees.map(emp => calculateDefaultPayroll(emp));
        setPayrolls(defaultPayroll);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // ลอจิกคำนวณเบี้ยขยัน โบนัส KPI และหัก ขาด/สาย อัตโนมัติจากฐานข้อมูล
  const calculateDefaultPayroll = (emp) => {
    // เงินเดือนขั้นต่ำเริ่มต้น: ช่าง 15,000 ฿, แอดมิน/อื่นๆ 18,000 ฿
    const base = emp.role === 'tech' ? 15000.00 : 18000.00;
    
    // 1. ดึงโบนัสอัตโนมัติอ้างอิงจากคะแนนประเมิน KPI ครั้งล่าสุดของเดือนนี้
    const empEval = evaluations.find(ev => ev.emp_uid === emp.uid);
    let bonus = 0.00;
    if (empEval) {
      const multiplier = parseFloat(empEval.bonus_multiplier) || 1.00;
      const adjustment = parseFloat(empEval.salary_adjustment) || 0.00;
      bonus = (base * (multiplier - 1.00)) + adjustment;
    }

    // 2. คำนวณหักอัตโนมัติจากการ ขาด ลา มา สาย ในเดือนที่เลือก
    let deduction = 0.00;
    const empAttendance = attendanceList.filter(log => {
      if (log.emp_uid !== emp.uid) return false;
      const [y, m] = log.log_date.split('-');
      const [targetM, targetY] = payrollMonth.split('-');
      return y === targetY && m === targetM;
    });

    empAttendance.forEach(log => {
      if (log.status === 'absent') {
        deduction += 500.00; // ขาดงาน หัก 500 บาท
      } else if (log.status === 'late') {
        deduction += 100.00; // มาสาย หัก 100 บาท
      }
    });

    const net = base + bonus - deduction;

    return {
      emp_uid: emp.uid,
      emp_name: emp.name,
      department: emp.department || 'ทีมช่างซ่อม',
      base_salary: base,
      bonus: bonus,
      deduction: deduction,
      net_salary: net < 0 ? 0 : net,
      status: 'unpaid',
      paidAt: null
    };
  };

  // บันทึกสถานะ ขาด ลา มา สาย รายคนลง SQL
  const handleSaveAttendance = async (emp) => {
    const status = attendanceStatus[emp.uid] || 'present';
    const notes = attendanceNotes[emp.uid] || '';

    try {
      const res = await fetch(`${API_URL}?action=save_attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emp_uid: emp.uid,
          emp_name: emp.name,
          log_date: attendanceDate,
          status: status,
          notes: notes
        })
      });
      const data = await res.json();
      if (data.success) {
        Swal.fire({
          title: 'บันทึกสำเร็จ!',
          text: `บันทึกการลงเวลาของ ${emp.name} เป็น "${status === 'present' ? 'มาทำงาน' : status === 'late' ? 'มาสาย' : status === 'leave' ? 'ลาพักร้อน' : 'ขาดงาน'}" เรียบร้อย`,
          icon: 'success',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 1500
        });
        fetchAttendanceLogs();
      }
    } catch (err) {
      Swal.fire('ข้อผิดพลาด', 'ไม่สามารถเชื่อมต่อฐานข้อมูลได้', 'error');
    }
  };

  // บันทึกเงินเดือนและสถานะการจ่ายเงิน
  const handleSavePayroll = async (payroll, index) => {
    try {
      const res = await fetch(`${API_URL}?action=save_employee_payroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emp_uid: payroll.emp_uid,
          emp_name: payroll.emp_name,
          month_year: payrollMonth,
          base_salary: parseFloat(payroll.base_salary),
          bonus: parseFloat(payroll.bonus),
          deduction: parseFloat(payroll.deduction),
          net_salary: parseFloat(payroll.net_salary),
          status: payroll.status,
        })
      });
      const data = await res.json();
      if (data.success) {
        Swal.fire('สำเร็จ! 💰', `บันทึกบัญชีเงินเดือนของ ${payroll.emp_name} เรียบร้อยแล้ว`, 'success');
        fetchPayrollData();
      }
    } catch (err) {
      Swal.fire('ข้อผิดพลาด', 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้', 'error');
    }
  };

  const handleUpdatePayrollField = (index, field, value) => {
    setPayrolls(prev => prev.map((item, idx) => {
      if (idx !== index) return item;
      const updated = { ...item, [field]: parseFloat(value) || 0.00 };
      updated.net_salary = updated.base_salary + updated.bonus - updated.deduction;
      return updated;
    }));
  };

  const handleTogglePaidStatus = (index, currentStatus) => {
    const nextStatus = currentStatus === 'paid' ? 'unpaid' : 'paid';
    setPayrolls(prev => prev.map((item, idx) => 
      idx === index ? { ...item, status: nextStatus } : item
    ));
  };

  // พิมพ์สลิปใบเสร็จรับเงินเดือน (Pay Slip)
  const handlePrintSlip = (payroll) => {
    Swal.fire({
      title: '🧾 ใบสำคัญการจ่ายเงินเดือน (Payslip)',
      html: `
        <div class="text-left font-mono text-xs border border-gray-300 p-4 rounded-xl bg-gray-50 text-gray-800 space-y-2">
          <p class="text-center font-bold text-sm">THUNDER MAXHOME CO., LTD.</p>
          <p class="text-center border-b pb-2">ใบแจ้งยอดเงินเดือนและโบนัสพนักงาน</p>
          <div class="grid grid-cols-2 pt-2 border-b pb-2">
            <div><b>ชื่อพนักงาน:</b> ${payroll.emp_name}</div>
            <div><b>แผนก:</b> ${payroll.department}</div>
            <div><b>งวดประจำเดือน:</b> ${payrollMonth}</div>
            <div><b>สถานะ:</b> <span class="${payroll.status === 'paid' ? 'text-green-600 font-bold' : 'text-red-500 font-bold'}">${payroll.status === 'paid' ? 'จ่ายเงินแล้ว' : 'ยังไม่ได้รับเงิน'}</span></div>
          </div>
          <div class="space-y-1.5 pt-2 border-b pb-2">
            <div class="flex justify-between"><span>เงินเดือนพื้นฐาน (Base Salary):</span> <span>${payroll.base_salary.toLocaleString()} ฿</span></div>
            <div class="flex justify-between text-green-600"><span>โบนัสประเมิน KPI (Bonus):</span> <span>+${payroll.bonus.toLocaleString()} ฿</span></div>
            <div class="flex justify-between text-red-500"><span>รายการหักเงิน ขาด/สาย (Deductions):</span> <span>-${payroll.deduction.toLocaleString()} ฿</span></div>
          </div>
          <div class="flex justify-between text-sm font-black pt-2">
            <span>รายรับสุทธิ (Net Salary):</span>
            <span class="text-blue-600">${payroll.net_salary.toLocaleString()} ฿</span>
          </div>
        </div>
      `,
      confirmButtonText: 'ปิดหน้าต่าง',
      confirmButtonColor: '#2563eb'
    });
  };

  if (!isAuthorized) {
    return <Navigate to="/admin/dashboard" />;
  }

  // คำนวณสรุปสถิติประจำวัน
  const totalCount = employees.length;
  const presentCount = Object.values(attendanceStatus).filter(s => s === 'present').length;
  const absentCount = Object.values(attendanceStatus).filter(s => s === 'absent').length;
  const leaveCount = Object.values(attendanceStatus).filter(s => s === 'leave').length;
  const lateCount = Object.values(attendanceStatus).filter(s => s === 'late').length;

  return (
    <div className="space-y-6 pb-20 animate-fade-in text-gray-800 dark:text-gray-100 font-sans text-left">
      
      {/* 🚀 Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-850 dark:text-white flex items-center gap-2">
            <span className="text-blue-500"><FiUsers /></span> ฝ่ายทรัพยากรบุคคล (HR Management Console)
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            ลงเวลา ขาด ลา มา สาย คำนวณเบี้ยขยัน อัปเดตเงินโบนัสสะสมร่วมกับประวัติการประเมิน KPI ขององค์กร
          </p>
        </div>
        
        {/* แถบสลับ Tab */}
        <div className="flex bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-xl p-1 shadow-sm">
          <button 
            onClick={() => setActiveTab('attendance')}
            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${activeTab === 'attendance' ? 'bg-blue-600 text-white shadow' : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white'}`}
          >
            📅 ลงเวลางาน ขาด ลา สาย
          </button>
          <button 
            onClick={() => setActiveTab('payroll')}
            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${activeTab === 'payroll' ? 'bg-blue-600 text-white shadow' : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white'}`}
          >
            💰 จัดการเงินเดือน & โบนัส
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* TAB 1: ลงเวลางาน ขาด ลา สาย (Attendance) */}
      {/* ========================================================================= */}
      {activeTab === 'attendance' && (
        <div className="space-y-6">
          
          {/* แถบค้นหาและสถิติสะสม */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2 bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl p-5 shadow-sm flex flex-col justify-between">
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">เลือกวันที่ลงบันทึกการทำงาน</label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-3.5 text-gray-400" />
                <input 
                  type="date"
                  value={attendanceDate}
                  onChange={(e) => setAttendanceDate(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-[#0f172a] text-gray-800 dark:text-white border border-gray-300 dark:border-[#334155] rounded-xl text-sm font-bold outline-none"
                />
              </div>
            </div>

            {/* การ์ดสถิติเข้างานวันนี้ */}
            <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl p-4 shadow-sm text-center">
              <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">เข้างานปกติ</span>
              <span className="text-3xl font-black text-emerald-500 block mt-1">{presentCount}</span>
              <span className="text-[10px] text-gray-400 block mt-1">จากทั้งหมด {totalCount} คน</span>
            </div>

            <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl p-4 shadow-sm text-center">
              <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">มาสาย</span>
              <span className="text-3xl font-black text-amber-500 block mt-1">{lateCount}</span>
              <span className="text-[10px] text-gray-400 block mt-1">หักรายหัวคนละ 100 ฿</span>
            </div>

            <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl p-4 shadow-sm text-center">
              <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">ขาดงาน / ลา</span>
              <span className="text-3xl font-black text-red-500 block mt-1">{absentCount + leaveCount}</span>
              <span className="text-[10px] text-gray-400 block mt-1">ขาดงานหักหัวละ 500 ฿</span>
            </div>
          </div>

          {/* รายชื่อพนักงานสแกนบันทึกผล */}
          <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-200 dark:border-[#1e2330] bg-gray-50/50 dark:bg-[#1b2132]/30">
              <h3 className="text-xs font-bold text-gray-450 dark:text-white uppercase tracking-wider">📋 รายชื่อพนักงานลงบันทึก ขาด ลา มา สาย</h3>
            </div>

            <div className="divide-y divide-gray-150 dark:divide-gray-800">
              {employees.length === 0 ? (
                <div className="text-center py-10 text-gray-400">ไม่พบรายชื่อพนักงาน</div>
              ) : (
                employees.map(emp => {
                  const currentStatus = attendanceStatus[emp.uid] || 'present';
                  return (
                    <div key={emp.uid} className="p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:bg-gray-50 dark:hover:bg-[#1e2330]/30 transition-colors">
                      {/* ข้อมูลพนักงาน */}
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 dark:bg-[#2d1b69] text-indigo-600 dark:text-[#c4b5fd] rounded-full flex items-center justify-center font-bold">
                          {emp.name?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-extrabold text-gray-800 dark:text-white text-sm">{emp.name}</p>
                          <p className="text-[10px] text-gray-400">แผนก: {emp.department || 'ไม่ระบุแผนก'} | บทบาท: {emp.role === 'tech' ? 'ช่างซ่อม' : 'แอดมิน'}</p>
                        </div>
                      </div>

                      {/* ตัวเลือกสถานะ ขาด ลา มา สาย */}
                      <div className="flex flex-wrap gap-2 w-full md:w-auto">
                        <button 
                          type="button"
                          onClick={() => setAttendanceStatus(prev => ({ ...prev, [emp.uid]: 'present' }))}
                          className={`px-3 py-1.5 rounded-lg font-bold text-xs transition-colors flex items-center gap-1
                            ${currentStatus === 'present' 
                              ? 'bg-emerald-500 text-white shadow' 
                              : 'bg-gray-100 dark:bg-slate-800 text-gray-650 dark:text-gray-400 hover:bg-gray-200'
                            }
                          `}
                        >
                          <FiUserCheck /> มาทำงาน
                        </button>
                        
                        <button 
                          type="button"
                          onClick={() => setAttendanceStatus(prev => ({ ...prev, [emp.uid]: 'late' }))}
                          className={`px-3 py-1.5 rounded-lg font-bold text-xs transition-colors flex items-center gap-1
                            ${currentStatus === 'late' 
                              ? 'bg-amber-500 text-white shadow' 
                              : 'bg-gray-100 dark:bg-slate-800 text-gray-650 dark:text-gray-400 hover:bg-gray-200'
                            }
                          `}
                        >
                          <FiClock /> สาย
                        </button>

                        <button 
                          type="button"
                          onClick={() => setAttendanceStatus(prev => ({ ...prev, [emp.uid]: 'leave' }))}
                          className={`px-3 py-1.5 rounded-lg font-bold text-xs transition-colors flex items-center gap-1
                            ${currentStatus === 'leave' 
                              ? 'bg-blue-600 text-white shadow' 
                              : 'bg-gray-100 dark:bg-slate-800 text-gray-650 dark:text-gray-400 hover:bg-gray-200'
                            }
                          `}
                        >
                          <FiBookOpen /> ลา
                        </button>

                        <button 
                          type="button"
                          onClick={() => setAttendanceStatus(prev => ({ ...prev, [emp.uid]: 'absent' }))}
                          className={`px-3 py-1.5 rounded-lg font-bold text-xs transition-colors flex items-center gap-1
                            ${currentStatus === 'absent' 
                              ? 'bg-red-500 text-white shadow' 
                              : 'bg-gray-100 dark:bg-slate-800 text-gray-650 dark:text-gray-400 hover:bg-gray-200'
                            }
                          `}
                        >
                          <FiUserX /> ขาดงาน
                        </button>
                      </div>

                      {/* หมายเหตุบันทึกและปุ่มเซฟ */}
                      <div className="flex gap-2 w-full md:w-80">
                        <input 
                          type="text"
                          value={attendanceNotes[emp.uid] || ''}
                          onChange={(e) => setAttendanceNotes(prev => ({ ...prev, [emp.uid]: e.target.value }))}
                          placeholder="บันทึกเพิ่มเติม (เช่น ลาป่วย, สายนอกพื้นที่)"
                          className="flex-1 p-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-xs outline-none focus:border-blue-500 text-gray-800 dark:text-white"
                        />
                        
                        <button
                          type="button"
                          onClick={() => handleSaveAttendance(emp)}
                          className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-3 py-2 rounded-lg text-xs transition shadow"
                        >
                          บันทึก
                        </button>
                      </div>

                    </div>
                  );
                })
              )}
            </div>

          </div>

        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: จัดการเงินเดือน & โบนัส (Payroll & Bonus) */}
      {/* ========================================================================= */}
      {activeTab === 'payroll' && (
        <div className="space-y-6 animate-fade-in">
          
          {/* ตัวคัดกรองรอบจ่ายงวดเดือน */}
          <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl p-5 shadow-sm dark:shadow-lg flex flex-col md:flex-row justify-between items-start md:items-center gap-4 transition-colors">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">เลือกรอบรอบจ่ายเงินเดือนพนักงาน</label>
              <select 
                value={payrollMonth}
                onChange={(e) => setPayrollMonth(e.target.value)}
                className="p-3 bg-gray-50 dark:bg-[#0f172a] text-gray-800 dark:text-white border border-gray-300 dark:border-[#334155] rounded-xl text-sm font-bold outline-none cursor-pointer"
              >
                <option value="05-2026">พฤษภาคม 2026 (05-2026)</option>
                <option value="06-2026">มิถุนายน 2026 (06-2026)</option>
                <option value="07-2026">กรกฎาคม 2026 (07-2026)</option>
                <option value="08-2026">สิงหาคม 2026 (08-2026)</option>
              </select>
            </div>
            
            <div className="text-right">
              <span className="text-[10px] text-gray-400 block font-bold uppercase tracking-wider">งบประมาณรวมประจำเดือน</span>
              <span className="text-2xl font-black text-blue-600 dark:text-blue-400 block mt-1">
                {payrolls.reduce((sum, p) => sum + p.net_salary, 0).toLocaleString()} ฿
              </span>
            </div>
          </div>

          {/* รายการบัญชีเงินเดือน */}
          <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl shadow-sm overflow-hidden transition-colors">
            <div className="p-5 border-b border-gray-200 dark:border-[#1e2330] bg-gray-50/50 dark:bg-[#1b2132]/30 flex justify-between items-center">
              <h3 className="text-xs font-bold text-gray-450 dark:text-white uppercase tracking-wider">💵 บัญชีจัดสรรค่าจ้างและโบนัสสะสมของพนักงาน</h3>
              <span className="text-[10px] text-orange-650 dark:text-orange-400 font-bold bg-orange-500/10 px-2 py-0.5 rounded animate-pulse">
                โบนัสลิงก์อัตโนมัติจากคะแนน KPI ล่าสุด
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-[9px] uppercase bg-gray-50 dark:bg-[#131825] text-gray-500 border-b border-gray-200 dark:border-[#1e2330]">
                  <tr>
                    <th className="px-5 py-4 font-bold">ชื่อพนักงาน / แผนก</th>
                    <th className="px-5 py-4 font-bold text-right">เงินเดือนฐาน (฿)</th>
                    <th className="px-5 py-4 font-bold text-right">โบนัสสะสม KPI (฿)</th>
                    <th className="px-5 py-4 font-bold text-right">หักขาด/สาย (฿)</th>
                    <th className="px-5 py-4 font-bold text-right">สุทธิรับจริง (฿)</th>
                    <th className="px-5 py-4 font-bold text-center">สถานะ</th>
                    <th className="px-5 py-4 font-bold text-center">บันทึก</th>
                    <th className="px-5 py-4 font-bold text-center">สลิป</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-150 dark:divide-gray-800">
                  {payrolls.map((payroll, idx) => (
                    <tr key={payroll.emp_uid} className="hover:bg-gray-50 dark:hover:bg-[#1e2330]/30 transition-colors">
                      <td className="px-5 py-4">
                        <p className="font-extrabold text-gray-850 dark:text-white text-sm">{payroll.emp_name}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{payroll.department}</p>
                      </td>
                      
                      {/* เงินฐาน */}
                      <td className="px-5 py-4 text-right">
                        <input 
                          type="number"
                          value={payroll.base_salary}
                          onChange={(e) => handleUpdatePayrollField(idx, 'base_salary', e.target.value)}
                          className="w-20 p-1.5 text-right font-bold bg-transparent border border-gray-300 dark:border-gray-700 rounded text-xs outline-none text-gray-800 dark:text-white"
                        />
                      </td>

                      {/* โบนัส */}
                      <td className="px-5 py-4 text-right">
                        <input 
                          type="number"
                          value={payroll.bonus}
                          onChange={(e) => handleUpdatePayrollField(idx, 'bonus', e.target.value)}
                          className="w-20 p-1.5 text-right font-bold bg-transparent border border-gray-300 dark:border-gray-700 rounded text-xs text-green-600 outline-none"
                        />
                      </td>

                      {/* รายการหักเงิน */}
                      <td className="px-5 py-4 text-right">
                        <input 
                          type="number"
                          value={payroll.deduction}
                          onChange={(e) => handleUpdatePayrollField(idx, 'deduction', e.target.value)}
                          className="w-20 p-1.5 text-right font-bold bg-transparent border border-gray-300 dark:border-gray-700 rounded text-xs text-red-500 outline-none"
                        />
                      </td>

                      {/* เงินเดือนสุทธิ */}
                      <td className="px-5 py-4 text-right font-black text-blue-600 dark:text-blue-400 text-sm">
                        {payroll.net_salary.toLocaleString()} ฿
                      </td>

                      {/* ปรับจ่ายแล้ว / ค้างจ่าย */}
                      <td className="px-5 py-4 text-center">
                        <button
                          onClick={() => handleTogglePaidStatus(idx, payroll.status)}
                          className={`px-2 py-1 rounded text-[9px] font-extrabold transition-colors
                            ${payroll.status === 'paid' 
                              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' 
                              : 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20'
                            }
                          `}
                        >
                          {payroll.status === 'paid' ? '✓ จ่ายแล้ว' : '⌛ ค้างจ่าย'}
                        </button>
                      </td>

                      {/* บันทึกเดี่ยว */}
                      <td className="px-5 py-4 text-center">
                        <button
                          onClick={() => handleSavePayroll(payroll, idx)}
                          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1.5 px-3 rounded-lg text-[9px] shadow"
                        >
                          บันทึก SQL
                        </button>
                      </td>

                      {/* พิมพ์ใบเสร็จสลิป */}
                      <td className="px-5 py-4 text-center">
                        <button
                          onClick={() => handlePrintSlip(payroll)}
                          className="bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300 p-2 rounded-lg"
                        >
                          <FiPrinter size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>

        </div>
      )}

    </div>
  );
}
